/**
 * lib/experiences/currencyConversion.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Semantic Intent Filters + Reference Currency Display V1.1, Parte F.
 *
 * Pure, no-I/O reference currency conversion — never a contractual price.
 * The provider's own checkout always confirms the final amount; this module
 * only ever computes what to DISPLAY. Callers must keep original_amount/
 * original_currency alongside whatever this returns (Restriction #6) — this
 * module never mutates or discards them.
 *
 * Conversion math (Task 5's own exact formula, USD as the pivot):
 *   amount_in_usd = original_amount / rates[original_currency]
 *   converted     = amount_in_usd * rates[selectedCurrency]
 *
 * Safety rules:
 *   - tip_based (GuruWalk "Aporte voluntario") is NEVER converted — callers
 *     must keep rendering that fixed copy themselves; this function returns
 *     amount=null for it so a caller can never accidentally compute a
 *     fabricated fixed price for a pay-what-you-want tour.
 *   - unknown/null price never crashes — amount=null, isReferenceConversion
 *     false.
 *   - original_currency missing from `rates`, or `rates` itself unavailable
 *     (e.g. GET /marketplace/fx-rates hasn't loaded yet) -> shows the
 *     ORIGINAL amount/currency untouched, isReferenceConversion=false, so a
 *     caller can render a plain price with no false "converted" claim.
 *   - selectedCurrency === original currency -> no-op passthrough, never a
 *     needless self-conversion that could introduce float rounding noise.
 */

export interface ConvertedPriceForDisplay {
  /** The amount to render, in `currency` — or null when there is nothing
   * safe to show (tip_based, or the item has no numeric price at all). */
  amount: number | null;
  /** The currency code `amount` is expressed in. Falls back to
   * `selectedCurrency` only when there is truly no other signal (both
   * amount and originalCurrency are absent). */
  currency: string;
  /** True ONLY when a real USD-pivot conversion happened — false for the
   * original-currency passthrough, tip_based, or unknown-price cases, so a
   * caller never shows a "reference conversion" disclaimer on a price that
   * was never actually converted. */
  isReferenceConversion: boolean;
}

export interface FxRatesMap {
  [currencyCode: string]: number;
}

/**
 * Convert one card's price for display in `selectedCurrency`. Never
 * throws — every branch has a safe, non-crashing fallback.
 */
export function convertPriceForDisplay(params: {
  originalAmount: number | null | undefined;
  originalCurrency: string | null | undefined;
  selectedCurrency: string;
  rates: FxRatesMap | null | undefined;
  isTipBased?: boolean;
}): ConvertedPriceForDisplay {
  const { originalAmount, originalCurrency, selectedCurrency, rates, isTipBased } = params;
  const targetCurrency = (selectedCurrency || "USD").toUpperCase();

  // tip_based: never a fixed price — callers render "Aporte voluntario"
  // themselves; this function must never fabricate a converted amount for it.
  if (isTipBased) {
    return { amount: null, currency: targetCurrency, isReferenceConversion: false };
  }

  // Unknown/null price — never crashes, never invents a number.
  if (originalAmount == null || !Number.isFinite(originalAmount)) {
    return { amount: null, currency: targetCurrency, isReferenceConversion: false };
  }

  const fromCurrency = (originalCurrency || "USD").toUpperCase();

  // Same currency already — no conversion needed, never a rounding-noise
  // self-conversion.
  if (fromCurrency === targetCurrency) {
    return { amount: originalAmount, currency: fromCurrency, isReferenceConversion: false };
  }

  // No rates loaded yet, or the original/target currency isn't one of the
  // 4 this backend publishes -> show the ORIGINAL price untouched. The
  // caller is expected to treat isReferenceConversion=false + currency !==
  // selectedCurrency as "couldn't convert, showing original" and render
  // the existing disclaimer copy accordingly.
  const fromRate = rates ? rates[fromCurrency] : undefined;
  const toRate = rates ? rates[targetCurrency] : undefined;
  if (!rates || !fromRate || !toRate) {
    return { amount: originalAmount, currency: fromCurrency, isReferenceConversion: false };
  }

  const amountInUsd = originalAmount / fromRate;
  const converted = amountInUsd * toRate;
  return { amount: converted, currency: targetCurrency, isReferenceConversion: true };
}

// ── Display formatting ──────────────────────────────────────────────────
// Hotfix: Global Currency Formatting + Exact Query Match Boost + No-
// Transfers Enforcement V1, Parte A.
//
// Root cause this replaces: the prior formatter did `${symbol}${Math.round
// (amount)}` with NO thousands separator and used the SAME "$" symbol for
// both USD and COP — a converted COP price like 410000 rendered as
// "$410000", ambiguous with (and visually mistakable for) a USD price.
//
// Fix is genuinely global: every currency this backend's /marketplace/
// fx-rates endpoint can ever publish is formatted via Intl.NumberFormat
// (a real, locale-aware, standards-based number formatter — never a
// currency-specific if-branch beyond picking which locale/prefix a code
// uses). Adding a 5th currency later means adding one CURRENCY_DISPLAY
// entry, never new formatting logic.
//
// Locale choice: "es-CO" already renders "." as the thousands separator
// and "," as the decimal separator — exactly Task 1's own COP requirement
// — and that SAME convention is what "EUR/BRL: respetar coma decimal
// cuando aplique" asks for too, so COP/EUR/BRL all share it. USD keeps
// "en-US" (comma thousands, period decimal) — its own existing, already-
// correct convention; Task 1 explicitly asks for zero USD regression.
//
// "COP $" (not just "$") is used as the COP prefix specifically to avoid
// the exact ambiguity-with-USD this hotfix exists to fix (Task 4:
// "evitar ambigüedad entre COP y USD").

const CURRENCY_DISPLAY: Readonly<Record<string, { prefix: string; locale: string }>> = {
  USD: { prefix: "$", locale: "en-US" },
  COP: { prefix: "COP $", locale: "es-CO" },
  EUR: { prefix: "€", locale: "es-CO" },
  BRL: { prefix: "R$", locale: "es-CO" },
};

/**
 * True when `amount`, rounded to the nearest cent, is not a whole number
 * — used to decide whether to show ",XX"/".XX" at all (Task 1: "COP con
 * centavos si existen" — never invents ",00" on a perfectly round price).
 * Rounds first so ordinary floating-point noise (e.g. 39.990000000001)
 * never spuriously counts as "has cents".
 */
function _hasMeaningfulCents(amount: number): boolean {
  return Math.round(amount * 100) % 100 !== 0;
}

export function formatConvertedPrice(amount: number, currency: string): string {
  const code = currency.toUpperCase();
  const display = CURRENCY_DISPLAY[code];

  // Unknown currency (not one of the 4 this backend publishes) — never
  // breaks the UI; falls back to the raw code as a prefix, same safe
  // shape this function has always used for an unrecognized code.
  if (!display) {
    return `${code} ${Math.round(amount)}`;
  }

  const showCents = _hasMeaningfulCents(amount);
  const formattedNumber = new Intl.NumberFormat(display.locale, {
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${display.prefix}${formattedNumber}`;
}
